import { useMemo, useState } from 'react';
import { Accordion as RadixAccordion } from 'radix-ui';
import { ChevronUp, Typography } from '@dreamteam-monorepo/ui';
import type { PlayerMatch } from '@/lib/network/players';
import { FixtureItem } from './FixtureItem';
import {
  buildLockedPlayerMatchStatRows,
  getLockedPlayerMatchBonusPoints,
  getLockedPlayerMatchPoints,
  type LockedPlayerBonusRow,
  type LockedPlayerMatchStatRow,
} from './lockedPlayerInfoModalData';

/** A card holds one fixture, so its accordion only ever has this one item. */
const STATS_ITEM_VALUE = 'stats';

interface StatRowProps {
  row: LockedPlayerMatchStatRow;
  expanded: boolean;
  onToggle: () => void;
}

const StatRow = ({ row, expanded, onToggle }: StatRowProps) => {
  const hasDetails = row.details && row.details.length > 0;

  return (
    <div className="border-b border-[--color-neutral-200] last:border-b-0">
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          onToggle();
        }}
        disabled={!hasDetails}
        className={`group flex w-full items-center py-[12px] text-left ${
          hasDetails ? 'cursor-pointer' : 'cursor-default'
        }`}
        aria-expanded={hasDetails ? expanded : undefined}
      >
        <div className="flex min-w-0 flex-1 items-center gap-[8px] pr-[16px]">
          <Typography
            variant={row.emphasized ? 'subheadline020' : 'paragraph010'}
            className={`min-w-0 text-sm text-[--color-neutral-900] ${
              row.emphasized ? 'font-bold' : 'font-normal'
            }`}
          >
            {row.label}
          </Typography>
          {hasDetails && (
            <ChevronUp
              className={`size-4 shrink-0 text-[--color-neutral-900] transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] ${
                expanded ? 'rotate-0' : 'rotate-180'
              }`}
              aria-hidden
            />
          )}
        </div>
        <Typography
          variant="subheadline020"
          className="w-[40px] shrink-0 text-center text-[--color-neutral-900]"
        >
          {row.total}
        </Typography>
        <Typography
          variant="subheadline020"
          className="w-[50px] shrink-0 text-center text-[#0072ee]"
        >
          {row.points}
        </Typography>
      </button>

      {hasDetails && expanded && (
        <div className="flex flex-col gap-[8px] pb-[12px]">
          {row.details.map((detail, detailIndex) => (
            <div key={`${detail.label}-${detailIndex}`} className="flex items-center">
              <Typography
                variant="paragraph010"
                className="min-w-0 flex-1 pr-[16px] text-sm text-[--color-neutral-900]"
              >
                {detail.label}
              </Typography>
              <Typography
                variant="subheadline010"
                className="w-[40px] shrink-0 text-center text-[--color-neutral-900]"
              >
                {detail.total}
              </Typography>
              <span className="w-[50px] shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatsTable = ({ rows }: { rows: LockedPlayerMatchStatRow[] }) => {
  const [expandedLabels, setExpandedLabels] = useState<Set<string>>(new Set());

  const toggleRow = (label: string) => {
    setExpandedLabels(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <div className="px-[12px] pb-[12px]">
      <div className="flex items-center border-b border-[--color-neutral-200] pb-[12px]">
        <Typography
          variant="paragraph000"
          className="min-w-0 flex-1 pr-[16px] text-[--color-neutral-600] text-[10px] leading-[10px]"
        ></Typography>
        <Typography
          variant="paragraph000"
          className="w-[40px] shrink-0 text-center text-[--color-neutral-600] text-[10px] leading-[10px]"
        >
          TOTAL
        </Typography>
        <Typography
          variant="paragraph000"
          className="w-[50px] shrink-0 text-center text-[--color-neutral-600] text-[10px] leading-[10px]"
        >
          POINTS
        </Typography>
      </div>
      {rows.map((row, index) => (
        <StatRow
          key={`${row.label}-${index}`}
          row={row}
          expanded={expandedLabels.has(row.label)}
          onToggle={() => toggleRow(row.label)}
        />
      ))}
    </div>
  );
};

interface MatchAccordionProps {
  match: PlayerMatch;
  defaultExpanded?: boolean;
  bonusRow?: LockedPlayerBonusRow | null;
  activeBoosterCode?: string | null;
}

/**
 * One fixture of the period: the match card, and under it an accordion holding
 * that match's own stat table (ENG-1134). Each match answers for itself, so a
 * double gameweek reads as two self-contained cards instead of one table with a
 * column per match.
 */
export const MatchAccordion = ({
  match,
  defaultExpanded = false,
  bonusRow,
  activeBoosterCode,
}: MatchAccordionProps) => {
  const statRows = useMemo(
    () => buildLockedPlayerMatchStatRows(match, activeBoosterCode, bonusRow),
    [match, activeBoosterCode, bonusRow]
  );

  const hasStats = statRows.length > 0;
  const matchPoints =
    getLockedPlayerMatchPoints(match.stats, activeBoosterCode) +
    getLockedPlayerMatchBonusPoints(bonusRow, match.matchId);

  const summary = (
    <div className="flex min-w-0 flex-1 items-center gap-[8px]">
      <Typography variant="paragraph010" className="truncate text-sm">
        {match.competitionLabel}
      </Typography>
    </div>
  );

  // A fixture the player has not played has nothing to open, so it keeps the
  // bar as a plain label rather than offering an empty accordion.
  if (!hasStats) {
    return (
      <div className="overflow-hidden rounded-[4px] bg-white">
        <FixtureItem fixtureProps={match} />
        <div className="flex w-full items-center gap-[8px] px-[12px] py-[10px]">{summary}</div>
      </div>
    );
  }

  return (
    <RadixAccordion.Root
      type="single"
      collapsible
      defaultValue={defaultExpanded ? STATS_ITEM_VALUE : undefined}
      className="overflow-hidden rounded-[4px] bg-white"
    >
      <RadixAccordion.Item value={STATS_ITEM_VALUE}>
        <FixtureItem fixtureProps={match} />

        <RadixAccordion.Header className="flex">
          <RadixAccordion.Trigger className="group flex w-full items-center gap-[8px] px-[12px] py-[10px] text-left outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#030033]">
            {summary}
            <div className="flex shrink-0 items-center gap-[8px]">
              <span className="rounded-[4px] bg-[#0072ee] px-[8px] py-[2px]">
                <Typography variant="subheadline020" className="text-[14px] font-bold text-white">
                  {matchPoints} PTS
                </Typography>
              </span>
              <ChevronUp
                className="size-5 rotate-180 text-[--color-neutral-900] transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=open]:rotate-0"
                aria-hidden
              />
            </div>
          </RadixAccordion.Trigger>
        </RadixAccordion.Header>

        <RadixAccordion.Content className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
          <StatsTable rows={statRows} />
        </RadixAccordion.Content>
      </RadixAccordion.Item>
    </RadixAccordion.Root>
  );
};
